import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  compatibiliteService,
  CONFIGURATION_LABELS,
  type LigneCompatibilite,
} from '@/services/compatibiliteService';
import { materielService } from '@/services/materielService';
import { typeMaterielService } from '@/services/typeMaterielService';
import type { ConfigurationJalon } from '@/types';
import { useAuthStore } from '@/store/authStore';
import toast from 'react-hot-toast';

const CONFIGURATIONS: ConfigurationJalon[] = ['contact_occupe', 'large_occupe', 'contact_libre', 'large_libre'];

/**
 * Grille de compatibilite type de materiel/vehicule x configuration x
 * jalon (SFD "planification des vehicules et equipages" v2, RG-VH-11).
 * Une case cochee = ce type de materiel est autorise sur ce jalon pour
 * cette configuration de poste. Prend effet immediatement (CA-VH-15),
 * sans redeploiement.
 *
 * Les colonnes ne sont PAS une liste figee en dur (deux versions anterieures
 * l'etaient encore, la derniere via une constante MATERIEL_TYPES partagee -
 * signale comme insuffisant par l'utilisateur : "les materiels sont figes
 * et ne sont pas tous representes"). Elles sont calculees dynamiquement a
 * partir de l'union de trois sources :
 * - le referentiel administrable TypeMateriel (Administration > Types de
 *   materiel) - source principale des libelles, et permet a un type tout
 *   juste cree d'apparaitre ici avant meme qu'un materiel ou une regle de
 *   compatibilite existe pour lui ;
 * - les types reellement presents dans le materiel existant (GET /materiels) ;
 * - les types ayant deja une regle de compatibilite enregistree (au cas ou
 *   le type aurait ete desactive/supprime du referentiel entre-temps, pour
 *   ne pas faire disparaitre une regle existante sans moyen de la gerer).
 */
export function CompatibilitePage() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const peutModifier = user?.roles.includes('ROLE_ADMIN') ?? false;
  const [configurationActive, setConfigurationActive] = useState<ConfigurationJalon>('contact_occupe');

  const { data: lignes, isLoading: isLoadingCompatibilites } = useQuery({
    queryKey: ['compatibilites'],
    queryFn: () => compatibiliteService.list(),
  });

  const { data: materiels, isLoading: isLoadingMateriels } = useQuery({
    queryKey: ['materiels', 'tous-pour-compatibilite'],
    queryFn: () => materielService.list(),
  });

  const { data: typesMateriel, isLoading: isLoadingTypes } = useQuery({
    queryKey: ['types-materiel'],
    queryFn: () => typeMaterielService.list(true),
  });

  const isLoading = isLoadingCompatibilites || isLoadingMateriels || isLoadingTypes;

  const updateMutation = useMutation({
    mutationFn: (ligne: { type: string; configuration: ConfigurationJalon; rang: number; autorise: boolean }) =>
      compatibiliteService.update([ligne]),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['compatibilites'] });
    },
    onError: (error: any) => toast.error(error.response?.data?.error || 'Erreur lors de la mise a jour'),
  });

  // Types affiches en colonnes : union du referentiel TypeMateriel, des
  // types reellement presents dans le materiel et des types deja presents
  // dans la grille de compatibilite - jamais une liste figee en dur (cf
  // note ci-dessus).
  const typesAffiches = useMemo(() => {
    const labels = new Map<string, string>();
    for (const type of typesMateriel || []) {
      labels.set(type.code, type.libelle);
    }
    for (const materiel of materiels || []) {
      if (!labels.has(materiel.type)) {
        labels.set(materiel.type, materiel.type);
      }
    }
    for (const ligne of lignes || []) {
      if (!labels.has(ligne.type)) {
        labels.set(ligne.type, ligne.type);
      }
    }
    return Array.from(labels.entries()).sort(([, a], [, b]) => a.localeCompare(b));
  }, [typesMateriel, materiels, lignes]);

  const lignesParRang = useMemo(() => {
    const parRang = new Map<number, Map<string, LigneCompatibilite>>();
    for (const ligne of lignes || []) {
      if (ligne.configuration !== configurationActive) continue;
      if (!parRang.has(ligne.rang)) parRang.set(ligne.rang, new Map());
      parRang.get(ligne.rang)!.set(ligne.type, ligne);
    }
    return Array.from(parRang.entries()).sort(([a], [b]) => a - b);
  }, [lignes, configurationActive]);

  const toggle = (ligne: LigneCompatibilite) => {
    updateMutation.mutate({
      type: ligne.type,
      configuration: ligne.configuration,
      rang: ligne.rang,
      autorise: !ligne.autorise,
    });
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
        <p className="text-sm text-gray-500 mt-3">Chargement de la grille de compatibilite...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Compatibilite materiel / jalons</h2>
      <p className="text-sm text-gray-500">
        Un type de materiel n'est propose pour un jalon que s'il est autorise ici, pour la
        configuration du poste concerne (RG-VH-11). Regle valable pour tous les postes de la
        configuration, sans exception par poste. Les colonnes refletent le referentiel
        Types de materiel (Administration &gt; Types de materiel) et le materiel reellement
        enregistre : un nouveau type cree y apparait automatiquement.
      </p>
      {!peutModifier && (
        <p className="text-xs text-amber-600">
          Lecture seule - seul un administrateur peut modifier cette grille.
        </p>
      )}

      <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
        {CONFIGURATIONS.map((config) => (
          <button
            key={config}
            type="button"
            onClick={() => setConfigurationActive(config)}
            className={`px-4 py-2 text-sm font-medium ${
              configurationActive === config ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            {CONFIGURATION_LABELS[config]}
          </button>
        ))}
      </div>

      <div className="card overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr>
              <th>Jalon (rang)</th>
              {typesAffiches.map(([type, label]) => (
                <th key={type} className="text-center whitespace-nowrap px-3">{label}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {lignesParRang.map(([rang, parType]) => (
              <tr key={rang}>
                <td className="font-medium">Jalon {rang}</td>
                {typesAffiches.map(([type]) => {
                  const ligne = parType.get(type);
                  if (!ligne) return <td key={type} className="text-center text-gray-300">-</td>;
                  return (
                    <td key={type} className="text-center">
                      <input
                        type="checkbox"
                        checked={ligne.autorise}
                        onChange={() => toggle(ligne)}
                        disabled={updateMutation.isPending || !peutModifier}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
            {typesAffiches.length === 0 && (
              <tr>
                <td className="text-center text-gray-500 py-8">
                  Aucun materiel enregistre - creez du materiel (Administration &gt; Materiel)
                  pour faire apparaitre des colonnes ici.
                </td>
              </tr>
            )}
            {typesAffiches.length > 0 && lignesParRang.length === 0 && (
              <tr>
                <td colSpan={typesAffiches.length + 1} className="text-center text-gray-500 py-8">
                  Aucune ligne pour cette configuration
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default CompatibilitePage;
